import {
  Add,
  DeleteOutline,
  FiberManualRecord as FiberManualRecordIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  ListItemIcon,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import {
  Controller,
  FieldValues,
  useFieldArray,
  useForm,
} from 'react-hook-form';

import { selectOrganisationInfos } from '@/domain/organisation/organisation.selectors';
import { ProjectVenue } from '@/domain/project-venue/entities/project-venue.entity';
import { bulkAddVenueDates } from '@/domain/project-venue-dates/actions/bulk-add-venue-date.action';
import { VenueDateBasic } from '@/domain/project-venue-dates/entities/project-venue-dates.entity';
import { selectProjectVenueIsCreatingDates } from '@/domain/project-venue-dates/project-venue-dates.selectors';
import { selectProjectVenuesPresetsItems } from '@/domain/project-venue-presets/project-venue-presets.selectors.ts';
import { CloseButton } from '@/ui/components/button/CloseButton';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { FSSecondaryButton } from '@/ui/components/button/FSSecondaryButton';
import { FSDateField } from '@/ui/components/input/FSDateField';
import { FSTextField } from '@/ui/components/input/FSTextField';
import { FSModal } from '@/ui/components/modal/FSModal';
import { FSModalTitle } from '@/ui/components/modal/FSModalTitle';
import { thStyle } from '@/ui/components/table/Table.utils';
import { useAppDispatch, useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { ColorsRaw } from '@/ui/theme/colors-raw';
import {
  isAtLeastStartDate,
  isMaxBeforeEndDate,
} from '@/ui/utils/form-validations';

type Inputs = {
  dates: { date: string; name: string; presetId: number | undefined }[];
};

export function ModalAddDates({
  open,
  onClose,
  project,
  initialDates,
  focusedMonth,
}: {
  open: boolean;
  onClose: () => void;
  project: ProjectVenue;
  initialDates: VenueDateBasic[];
  focusedMonth: string | undefined;
}) {
  const dispatch = useAppDispatch();
  const organisation = useAppSelector(selectOrganisationInfos);
  const presets = useAppSelector(selectProjectVenuesPresetsItems);

  const isCreatingDates = useAppSelector(selectProjectVenueIsCreatingDates);

  const emptyDatesField: Inputs['dates'] = [
    { date: '', name: '', presetId: presets[0]?.id },
  ];

  const { formState, control, register, setValue, handleSubmit, reset } =
    useForm<Inputs>({
      defaultValues: {
        dates: emptyDatesField,
      },
    });

  const { fields, remove, insert } = useFieldArray({
    control,
    name: 'dates',
  });

  const { errors } = formState;

  const [dateFieldReferenceDate, setDateFieldReferenceDate] = useState<
    Dayjs | undefined
  >(undefined);

  useEffect(() => {
    // when receiving project object, use start date as reference date
    if (project.startDate) {
      setDateFieldReferenceDate(dayjs(project.startDate));
    }
  }, [project.startDate]);

  useEffect(() => {
    // when receiving focusedMonth, use it as reference date
    if (focusedMonth) {
      setDateFieldReferenceDate(dayjs(focusedMonth));
    }
  }, [focusedMonth]);

  useEffect(() => {
    if (initialDates.length === 0) {
      setValue('dates', emptyDatesField);
    } else {
      // init dates
      const datesField = initialDates.map((date) => ({
        date: dayjs(date.date).isValid() ? dayjs(date.date).toISOString() : '',
        name: date.name,
        presetId: presets[0]?.id,
      }));
      setValue('dates', datesField);
      // init dateFieldReferenceDate
      const referenceDate = dayjs(initialDates[initialDates.length - 1].date);
      setDateFieldReferenceDate(referenceDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDates]);

  const updateDateFieldReferenceDateOnChangeDate = (value: Dayjs | null) => {
    if (value) {
      setDateFieldReferenceDate(value);
    }
  };

  const handleAddDate = () => {
    insert(fields.length, { date: '', name: '', presetId: presets[0]?.id });
  };

  const handleFormSubmit = async (data: FieldValues) => {
    if (data.dates && data.dates.length > 0) {
      const dates = data.dates.map(
        (date: { date: Dayjs; name: string; presetId: number }) => {
          return {
            date: dayjs(date.date).toISOString(),
            name: date.name,
            presetId: date.presetId,
          };
        },
      );

      const result = await dispatch(
        bulkAddVenueDates({
          dates,
          organisationId: organisation.id,
          projectId: project.id,
        }),
      ).unwrap();

      if (result.success) {
        remove();
        insert(0, { date: '', name: '', presetId: presets[0]?.id });
        resetAndClose();
      }
    }
  };

  const resetAndClose = () => {
    setDateFieldReferenceDate(dayjs(project.startDate));
    reset();
    onClose();
  };

  const presetOptions: Array<React.JSX.Element> = [];
  for (const preset of presets) {
    presetOptions.push(
      <MenuItem key={preset.id} value={preset.id}>
        {preset.presetColor && (
          <ListItemIcon sx={{ minWidth: 28 }}>
            <FiberManualRecordIcon
              sx={{ color: preset.presetColor, fontSize: 16 }}
            />
          </ListItemIcon>
        )}
        {preset.presetTitle}
      </MenuItem>,
    );
  }

  return (
    <FSModal
      open={open}
      onClose={resetAndClose}
      maxWidth={1000}
      disableCloseOnBackdropClick={true}
    >
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between">
          <FSModalTitle
            title={translate('VenueDates.AddDatesModal.add-dates')}
          />
          <CloseButton onClick={resetAndClose} />
        </Stack>

        <form
          style={{ width: '100%' }}
          onSubmit={handleSubmit(handleFormSubmit)}
        >
          <Table align="right">
            <TableHead>
              <TableRow>
                <TableCell style={thStyle} />
                <TableCell style={thStyle}>
                  {translate('VenueDates.AddDatesModal.date')}
                </TableCell>
                <TableCell style={thStyle}>
                  {translate('VenueDates.AddDatesModal.event-name')}
                </TableCell>
                <TableCell style={thStyle}>
                  {translate('VenueDates.AddDatesModal.venue-config')}
                </TableCell>
                <TableCell style={thStyle} />
              </TableRow>
            </TableHead>
            <TableBody>
              {fields?.map((date, index) => (
                <TableRow key={date.id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell sx={{ maxWidth: 150 }}>
                    <Controller
                      name={`dates.${index}.date`}
                      control={control}
                      rules={{
                        required: true,
                        validate: {
                          isAtLeastStartDate: (date) =>
                            isAtLeastStartDate(date, project?.startDate || ''),
                          isMaxBeforeEndDate: (date) =>
                            isMaxBeforeEndDate(date, project?.endDate || ''),
                        },
                      }}
                      render={({ field }) => (
                        <FSDateField
                          value={field.value}
                          name={field.name}
                          onChange={(value: Dayjs | null) => {
                            updateDateFieldReferenceDateOnChangeDate(value);
                            field.onChange(value);
                          }}
                          referenceDate={dateFieldReferenceDate}
                          minDate={dayjs(project?.startDate || '')}
                          maxDate={dayjs(project?.endDate || '')}
                          error={!!errors.dates?.[index]?.date}
                          helperText={errors.dates?.[index]?.date?.message}
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Box display="flex" alignItems="center">
                      <FSTextField
                        register={{
                          ...register(`dates.${index}.name`, {
                            required: translate('Form.fill-the-field'),
                          }),
                        }}
                        //@ts-ignore
                        error={!!errors.dates?.[index]?.name}
                        //@ts-ignore
                        helperText={errors.dates?.[index]?.name?.message}
                      />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Box display="flex" alignItems="center">
                      <FSTextField
                        select
                        register={{
                          ...register(`dates.${index}.presetId`, {
                            required: translate('Form.fill-the-field'),
                          }),
                        }}
                        defaultValue={date.presetId}
                        sx={{ width: '100%' }}
                      >
                        {presetOptions}
                      </FSTextField>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ px: 0 }}>
                    <Box display="flex" justifyContent="end">
                      <Button onClick={() => remove(index)}>
                        <DeleteOutline sx={{ color: ColorsRaw.basic.White }} />
                      </Button>
                    </Box>
                    <Box></Box>
                  </TableCell>
                </TableRow>
              ))}

              <TableRow>
                <TableCell colSpan={5}>
                  <Box display={'flex'} alignItems={'center'}>
                    <FSSecondaryButton onClick={handleAddDate}>
                      <Add
                        sx={{
                          color: ColorsRaw.main.Navy_Light,
                          fontSize: 18,
                          mr: 1,
                        }}
                      />
                      {translate('VenueDates.AddDatesModal.add-date')}
                    </FSSecondaryButton>
                  </Box>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </form>

        <Box display={'flex'} alignItems={'center'} justifyContent={'end'}>
          <FSSecondaryButton sx={{ ml: 1 }} onClick={resetAndClose}>
            {translate('Form.cancel')}
          </FSSecondaryButton>
          <FSPrimaryButton
            sx={{ ml: 1 }}
            disabled={isCreatingDates}
            onClick={handleSubmit(handleFormSubmit)}
          >
            {translate('Form.validate')}
          </FSPrimaryButton>
        </Box>
      </Stack>
    </FSModal>
  );
}
